import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, Redirect, useRouteMatch } from 'react-router-dom';

import Header from './Header';
import PersonSelector from './PersonSelector';
import DevelopmentPlan from './DevelopmentPlan';
import Puzzles from './Puzzles';
import Shapes from './Shapes';
import { useDevTabsAccess } from './useDevTabsAccess';
import { useDevPassAccess } from './DevelopmentPlan/useDevPassAccess';
import SessionsV2 from './dashboard_v2';
import './dashboard_v2/dashboardV2.css';

import {
  selectors,
  connectToKlassSocket,
  disconnectFromKlassSocket,
} from 'state';

const TraineeDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const token = useSelector(selectors.token) as string;
  const selectedPerson = useSelector(selectors.selectedPerson);
  // Development Plan + Games tabs are shown to trainees the admin has granted
  // access (per-user `can_see_devplan`, served on the klasses payload), OR to
  // developers via the `?devTabs=1` override. While hidden, the Switch falls
  // through to the redirect below, so direct navigation to these routes bounces
  // back to the dashboard. Shapes stays dev-override-only (a layout sandbox, not
  // exposed to flagged trainees).
  const devOverride = useDevTabsAccess();
  const canSeeDevplan = useSelector(selectors.canSeeDevplan);
  const showDevplanTabs = canSeeDevplan || devOverride;
  // Capture `?devPass=1|0` here, at the top of the dashboard tree. The Development
  // Plan view consumes the flag but mounts too late to see the param itself — the
  // redirect below and the Header's <Link>s both drop the query string first.
  useDevPassAccess();
  // The command-center Dashboard (SessionsV2) is the default — and only —
  // Dashboard view. The classic sessions table (./Sessions) is kept in the tree
  // for reference/revert but is no longer imported or mounted. Only the exact
  // `/dashboard` route locks the viewport for the command-center shell; the dev
  // tabs keep the normal padded, page-scrolled body.
  const onDashboardRoute = useRouteMatch({ path, exact: true });
  const lockFrame = !!onDashboardRoute;

  useEffect(() => {
    dispatch(connectToKlassSocket(token));

    return () => {
      dispatch(disconnectFromKlassSocket(token));
    };
  }, [dispatch, token]);

  return (
    <div className={`trainee-dash${lockFrame ? ' trainee-dash--locked' : ''}`}>
      <Header />
      <div
        className={`trainee-dash__body${
          lockFrame ? ' trainee-dash__body--locked' : ''
        }`}
      >
        <Switch>
          <Route exact path={path}>
            {/* Person selection lives only on the Dashboard tab — the persons
                list is fetched there (via SessionsV2). Rendering it on the
                self-contained Development Plan / Games tabs would pop an
                optionless modal on a direct reload. */}
            <PersonSelector visible={!selectedPerson} />
            <SessionsV2 />
          </Route>
          {showDevplanTabs && (
            <Route path={`${path}/development-plan`}>
              <DevelopmentPlan />
            </Route>
          )}
          {showDevplanTabs && (
            <Route path={`${path}/games`}>
              <Puzzles />
            </Route>
          )}
          {devOverride && (
            <Route path={`${path}/shapes`}>
              <Shapes />
            </Route>
          )}
          <Redirect to={path} />
        </Switch>
      </div>
    </div>
  );
};

export default TraineeDashboard;
